"use client";

/**
 * ShareDialog — invite collaborators by email AND generate shareable links.
 * Ported from reactjs/src/components/ShareDialog.tsx.
 */
import { useState } from "react";
import { getIdToken } from "@/platform/firebase/auth";
import {
  addCollaborator,
  removeCollaborator,
  generateShareToken,
  regenerateShareToken,
} from "@/platform/firebase/firestore/diagnostics";

const DIAGNOSIS_API = process.env.NEXT_PUBLIC_DIAGNOSIS_API ?? "";

/** Share link base — uses the browser's current origin (works on localhost + prod) */
function getShareBase(): string {
  if (typeof window !== "undefined") return window.location.origin;
  return process.env.NEXT_PUBLIC_APP_URL ?? "http://localhost:3000";
}

export interface DiagnosticCollaborator {
  email: string;
  uid: string | null;
}

interface Props {
  sessionId:    string;
  sessionTitle: string;
  sharedWith:   DiagnosticCollaborator[];
  /** Existing share token, if one has already been generated */
  existingShareToken?: string;
  onClose:      () => void;
  onCollaboratorAdded:   (email: string) => void;
  onCollaboratorRemoved: (email: string) => void;
  /** Called when a share token is generated or regenerated */
  onShareTokenChanged?: (token: string) => void;
}

export function ShareDialog({
  sessionId,
  sessionTitle,
  sharedWith,
  existingShareToken,
  onClose,
  onCollaboratorAdded,
  onCollaboratorRemoved,
  onShareTokenChanged,
}: Props) {
  const [name,  setName]  = useState("");
  const [email, setEmail] = useState("");
  const [busy,  setBusy]  = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [collaborators, setCollaborators] =
    useState<DiagnosticCollaborator[]>(sharedWith);

  // ── Share link state ───────────────────────────────────────────────────────
  const [shareToken, setShareToken]       = useState<string | null>(existingShareToken ?? null);
  const [copied, setCopied]               = useState(false);
  const [shareBusy, setShareBusy]         = useState(false);
  const [shareError, setShareError]       = useState<string | null>(null);
  const [regenerating, setRegenerating]   = useState(false);

  const shareUrl = shareToken
    ? `${getShareBase()}/diagnostics/shared/${shareToken}`
    : null;

  // ── Invite collaborator ────────────────────────────────────────────────────

  const handleInvite = async () => {
    const trimEmail = email.trim();
    const trimName  = name.trim();
    if (!trimEmail || !trimName) return;

    if (collaborators.some((c) => c.email === trimEmail)) {
      setError("Already invited");
      return;
    }

    setBusy(true);
    setError(null);
    try {
      const token     = await getIdToken() ?? "";

      // Write collaborator to Firestore first (was missing in migration)
      await addCollaborator(sessionId, trimEmail);

      const newCollab: DiagnosticCollaborator = { email: trimEmail, uid: null };
      setCollaborators((prev) => [...prev, newCollab]);
      onCollaboratorAdded(trimEmail);

      // Fire-and-forget invite email
      fetch(`${DIAGNOSIS_API}/api/email/send-share-invite`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          collaborator_email: trimEmail,
          collaborator_name:  trimName,
          diagnostic_title:   sessionTitle,
          session_id:         sessionId,
        }),
      }).catch(() => {});

      setName("");
      setEmail("");
    } catch {
      setError("Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const handleRevoke = async (collab: DiagnosticCollaborator) => {
    await removeCollaborator(sessionId, collab.email);
    setCollaborators((prev) => prev.filter((c) => c.email !== collab.email));
    onCollaboratorRemoved(collab.email);
  };

  // ── Share link handlers ────────────────────────────────────────────────────

  const handleCopyLink = async () => {
    if (shareUrl) {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      return;
    }

    // Generate token first
    setShareBusy(true);
    setShareError(null);
    try {
      const token = await generateShareToken(sessionId);
      setShareToken(token);
      onShareTokenChanged?.(token);
      const url = `${getShareBase()}/diagnostics/shared/${token}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setShareError("Failed to generate link");
    } finally {
      setShareBusy(false);
    }
  };

  const handleRegenerate = async () => {
    if (!confirm("This will invalidate the previous link. Continue?")) return;
    setRegenerating(true);
    setShareError(null);
    try {
      const token = await regenerateShareToken(sessionId);
      setShareToken(token);
      onShareTokenChanged?.(token);
    } catch {
      setShareError("Failed to regenerate link");
    } finally {
      setRegenerating(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Share diagnostic"
      style={{
        position:       "fixed",
        inset:          0,
        zIndex:         200,
        background:     "rgba(0,0,0,0.75)",
        display:        "flex",
        alignItems:     "center",
        justifyContent: "center",
        padding:        "24px",
        backdropFilter: "blur(6px)",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background:   "var(--paper-1)",
          border:       "1px solid var(--line-strong)",
          borderRadius: "16px",
          width:        "100%",
          maxWidth:     "440px",
          padding:      "32px",
          position:     "relative",
          color:        "var(--ink)",
          maxHeight:    "90vh",
          overflowY:    "auto",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close */}
        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position:   "absolute", top: "16px", right: "16px",
            background: "none", border: "none", cursor: "pointer",
            color: "var(--ink-mute)", fontSize: "20px",
          }}
        >
          ✕
        </button>

        <h3 style={{ margin: "0 0 4px", fontSize: "18px" }}>Share Diagnostic</h3>
        <p style={{ margin: "0 0 24px", fontSize: "13px", opacity: 0.5 }}>{sessionTitle}</p>

        {/* ── Shareable link section ───────────────────────────────────────── */}
        <div style={{
          border:       "1px solid var(--line-strong)",
          borderRadius: "12px",
          padding:      "16px",
          marginBottom: "20px",
          background:   "var(--paper-2)",
        }}>
          <p style={{ margin: "0 0 4px", fontSize: "13px", fontWeight: 600 }}>
            Shareable link
          </p>
          <p style={{ margin: "0 0 12px", fontSize: "12px", color: "var(--ink-mute)", lineHeight: 1.5 }}>
            Anyone with this link can take this assessment for free. No account required until they complete it.
          </p>

          {shareError && (
            <p style={{ color: "#ef4444", fontSize: "12px", margin: "0 0 8px" }}>{shareError}</p>
          )}

          <div style={{ display: "flex", gap: "8px" }}>
            <button
              onClick={handleCopyLink}
              disabled={shareBusy}
              style={{
                flex:           1,
                padding:        "10px 14px",
                background:     "#5B6CFF",
                border:         "none",
                borderRadius:   "8px",
                color:          "#fff",
                fontWeight:     600,
                fontSize:       "13px",
                cursor:         shareBusy ? "not-allowed" : "pointer",
                opacity:        shareBusy ? 0.6 : 1,
                display:        "flex",
                alignItems:     "center",
                justifyContent: "center",
                gap:            "6px",
              }}
            >
              {shareBusy ? (
                "Generating…"
              ) : copied ? (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  Copied!
                </>
              ) : (
                <>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                  Copy Link
                </>
              )}
            </button>

            {shareToken && (
              <button
                onClick={handleRegenerate}
                disabled={regenerating}
                style={{
                  padding:      "10px 14px",
                  background:   "var(--paper-1)",
                  border:       "1px solid var(--line-strong)",
                  borderRadius: "8px",
                  color:        "var(--ink-soft)",
                  fontSize:     "13px",
                  cursor:       regenerating ? "not-allowed" : "pointer",
                  opacity:      regenerating ? 0.6 : 1,
                  whiteSpace:   "nowrap",
                }}
                title="Generate a new link (invalidates the previous one)"
              >
                {regenerating ? "…" : "Regenerate"}
              </button>
            )}
          </div>
        </div>

        {/* ── Divider ──────────────────────────────────────────────────────── */}
        <div style={{
          borderTop:  "1px solid var(--line)",
          paddingTop: "16px",
          marginBottom: "16px",
        }}>
          <p style={{ margin: "0 0 12px", fontSize: "13px", fontWeight: 600, opacity: 0.6 }}>
            Or invite by email
          </p>
        </div>

        {/* ── Email invite inputs ──────────────────────────────────────────── */}
        <div style={{ display: "flex", flexDirection: "column", gap: "10px", marginBottom: "12px" }}>
          <input
            type="text"
            placeholder="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            style={inputStyle}
          />
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleInvite()}
            style={inputStyle}
          />
          {error && (
            <p style={{ color: "#ef4444", fontSize: "12px", margin: 0 }}>{error}</p>
          )}
          <button
            onClick={handleInvite}
            disabled={!name.trim() || !email.trim() || busy}
            style={{
              padding:      "11px",
              background:   "#5B6CFF",
              border:       "none",
              borderRadius: "10px",
              color:        "#fff",
              fontWeight:   600,
              fontSize:     "14px",
              cursor:       busy ? "not-allowed" : "pointer",
              opacity:      (!name.trim() || !email.trim() || busy) ? 0.6 : 1,
            }}
          >
            {busy ? "Inviting…" : "Invite"}
          </button>
        </div>

        {/* Current collaborators */}
        {collaborators.length > 0 && (
          <div style={{ borderTop: "1px solid var(--line)", paddingTop: "16px" }}>
            <p style={{ margin: "0 0 12px", fontSize: "13px", fontWeight: 600, opacity: 0.6 }}>
              Shared with
            </p>
            {collaborators.map((c) => (
              <div
                key={c.email}
                style={{
                  display:        "flex",
                  alignItems:     "center",
                  justifyContent: "space-between",
                  padding:        "8px 0",
                  fontSize:       "13px",
                  borderBottom:   "1px solid var(--line)",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span>{c.email}</span>
                  {c.uid !== null && (
                    <span style={{ color: "#22c55e", fontSize: "11px" }}>✓ Joined</span>
                  )}
                </div>
                <button
                  onClick={() => handleRevoke(c)}
                  aria-label={`Remove ${c.email}`}
                  style={{
                    background: "none", border: "none", cursor: "pointer",
                    color: "#ef4444", fontSize: "18px", lineHeight: 1, padding: "0 4px",
                  }}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const inputStyle: React.CSSProperties = {
  padding:      "10px 14px",
  background:   "var(--paper-2)",
  border:       "1px solid var(--line-strong)",
  borderRadius: "8px",
  color:        "var(--ink)",
  fontSize:     "14px",
  outline:      "none",
  width:        "100%",
  boxSizing:    "border-box",
};
